import axios from 'axios';
import API from '../../../services/http';
import { 
    SolutrakResponse,
    UptimeDataPoint,
    DoorCycleCountsDataPoint,
    RunCountsDataPoint,
    HourlyCarCallDataPoint,
    DoorCyclePerFloorDataPoint,
    LoadLevelDataPoint,
    ControllerTemperatureDataPoint
} from '../../../types/performance';
import { UpDownDirectionMinutesDataPoint } from '../../../types/device-statistics';

// --- Interfaces based on API responses ---

export interface ApiInfoMessage {
    direction: string; // Example: "10" (Up), "20" (Down), "30" (Stopped) - Needs mapping from number codes
    door: string;      // Example: "10" (Open), "20" (Closed) - Needs mapping from number codes
    location: string;  // Current floor, Example: "1"
    status: number;    // Example: 30 (Device status from infoMessage, not to be confused with ApiDeviceInfo.status)
    temperature: string; // Example: "35"
    other: string | null; // Example: null
    // Add other relevant fields from infoMessage if needed
}

export interface ApiDeviceEvent {
    id: number; // Example: 2550125
    status: number; // Consider mapping to a string status if meanings are known, Example: 10
    date: string; // ISO date string, Example: "2025-1-9T14:24:35.4107109Z"
    code: number; // Example: 2550125
    description: string; // Example: "Listening for remote event ..."
    activityId: number; // Example: 0
    // Fields below were in Postman description for deviceEvents but null in example JSON or part of parent object in example.
    // Making them optional for robustness.
    deviceId?: number; // ID of the device associated with the event
    deviceName?: string | null; // Name of the device associated with the event
    bankName?: string | null;   // Name of the bank associated with the event
}

export interface ApiDeviceInfo {
    deviceId: number; // Example: 359
    deviceName: string; // Example: "A #60262"
    deviceInstallationNumber?: string; // Example: "60262"
    deviceType?: string; // Example: "Elevator"
    contractor?: string; // Example: "Riverside Elevators Inc."
    status?: number; // Device's own status (distinct from infoMessage.status), Example: 30
    latitude?: number; // Example: 42.3134385
    longitude?: number; // API example in Postman uses "longitute", assuming typo for "longitude"
    cameraUrl?: string; // Example: "http://192.168.1.10/359"
    restartPersonal?: string; // Example: "Mechanic"
    infoMessage: ApiInfoMessage; // Contains live operational data like direction, door, current floor, temp
    deviceEvents: ApiDeviceEvent[]; // This might be extensive, confirm if needed for basic info

    // Denormalized fields found in Postman example's deviceInfo object (redundant, but present in API response)
    bankId?: number; // Example: 125
    bankName?: string; // Example: "Passenger"
    buildingId?: number; // Example: 85
    buildingName?: string; // Example: "Agnes MacPhail Manor - 860 Mercer"
    buildingAddress?: string; // Example: "860 Mercer Street"
    city?: string; // Example: "Windsor"
    // Add other relevant fields from deviceInfo if identified from API or future needs
}

export interface ApiBankInfo {
    bankId: number;
    bankName: string;
    deviceInfos: ApiDeviceInfo[];
    // Add other relevant fields from bankInfo
}

export interface ApiBuildingInfo {
    buildingId: number; // Example: 85
    buildingName: string; // Example: "Agnes MacPhail Manor - 860 Mercer"
    buildingAddress: string; // Renamed from 'address' to match Postman, Example: "860 Mercer Street"
    city: string; // Example: "Windsor"
    latitude: number; // Example: 42.314438499999994
    longitude: number; // API example in Postman uses "longitute", assuming typo
    bankInfos: ApiBankInfo[];

    // Fields below are not present in the getBuildingInfos Postman example response for the top-level building object.
    // Making them optional to reflect that they might come from other endpoints or not be universally present.
    postalCode?: string;
    country?: string;
    phone?: string;
    contactName?: string;
    contactEmail?: string;
    notes?: string;
    status?: number; // Building's own status (distinct from device status), meaning needs clarification
}

// API Response structure for getBuildingInfos
export interface GetBuildingInfosResponse {
    IsSuccess: boolean;
    Message: string;
    Data?: ApiBuildingInfo[];
}

// TypeScript interfaces for API responses for Events and Traffic
// Note: The structure of ApiEvent could not be fully verified from the Postman collection
// due to missing response examples or detailed descriptions for the /events/getEvents endpoint.
// The fields below are based on common event patterns and previous assumptions and may need adjustment.
export interface ApiEvent {
  EventId: string; // Unique identifier for the event
  EventType: string; // e.g., "Maintenance", "Fault", "System", "Alarm"
  EventSeverity: string; // e.g., "Critical", "Major", "Minor", "Warning", "Info"
  EventMessage: string; // Detailed message or description of the event
  Timestamp: string; // ISO date string of when the event occurred
  DeviceId: string; // Identifier for the device that generated the event. Type (string/number) needs confirmation.
  DeviceName: string; // Name of the device
  // Potential other fields based on common event structures (verify with actual API response):
  // BuildingId?: number | string;
  // BankId?: number | string;
  // EventCode?: string | number; // A specific code for the event type
  // Acknowledged?: boolean;
  // UserId?: string; // User who acknowledged or is related to the event
  // Add any other relevant fields from the actual API response when known
}

// Speculative interface: Represents stop data for an elevator on a specific floor
export interface ApiTrafficStop {
    floor: string | number; // Floor identifier
    count: number;          // Number of stops on this floor
}

// Speculative interface: Represents traffic density data for a specific floor
export interface ApiTrafficFloorDensity {
    floor: string | number; // Floor identifier
    goingIn: number;        // Count of people going in at this floor
    gettingOut: number;     // Count of people getting out at this floor
}

// Speculative interface: Represents traffic data for a single elevator
export interface ApiTrafficElevatorData {
    elevator: string | number;      // Elevator identifier (e.g., "1", "A")
    mostUsedFloor: string | number; // The floor most frequently used by this elevator
    stops: ApiTrafficStop[];        // Array of stop counts per floor
    density: ApiTrafficFloorDensity[]; // Array of density data per floor
}

// Speculative: Main interface for traffic density API response, expected to be an array.
// NOTE: This structure is based on frontend usage in NewAlternateView.tsx's Traffic tab
// and is NOT CONFIRMED by the Solutrak API Postman collection.
// The actual API endpoint and response structure for traffic density are TBD.
export type ApiTrafficDensityData = ApiTrafficElevatorData[];

// --- Exported API functions ---

/**
 * Fetches building information including banks and devices.
 */
export const getBuildingInfos = async (): Promise<ApiBuildingInfo[]> => {
    try {
        const response = await API.get<GetBuildingInfosResponse>('/buildings/getBuildingInfos');
        if (response.data?.IsSuccess) {
            return response.data?.Data ?? [];
        } else {
            const errorMessage = response.data?.Message ?? 'Failed to fetch building infos';
            console.error('Failed to fetch building infos:', errorMessage);
            throw new Error(errorMessage);
        }
    } catch (error) {
        console.error('Error in getBuildingInfos:', error);
        if (axios.isAxiosError(error)) {
            const errorMessage = error.response?.data?.Message ?? error.message ?? 'Network error fetching building infos';
            throw new Error(errorMessage);
        } else if (error instanceof Error) {
            throw error; // Re-throw if already an Error instance
        }
        throw new Error('An unexpected error occurred while fetching building infos');
    }
};

export const getEvents = async (
  buildingId: number,
  fromDate: string, // Expected format YYYY-MM-DD
  toDate: string,   // Expected format YYYY-MM-DD
  eventType?: string,
  eventSeverity?: string,
  deviceId?: string
): Promise<ApiEvent[]> => {
  try {
    const params: Record<string, any> = {
      buildingId,
      fromDate,
      toDate,
    };
    if (eventType) params.eventType = eventType;
    if (eventSeverity) params.eventSeverity = eventSeverity;
    if (deviceId) params.deviceId = deviceId;

    const response = await API.get<{ IsSuccess: boolean; Data?: ApiEvent[]; Message?: string }>('/events/getEvents', {
      params,
    });

    if (response.data?.IsSuccess) {
      return response.data?.Data ?? [];
    } else {
      const errorMessage = response.data?.Message ?? 'Failed to fetch events';
      console.error(errorMessage, response.data);
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('Error in getEvents:', error);
    if (axios.isAxiosError(error)) {
      const errorMessage = error.response?.data?.Message ?? error.message ?? 'Network error fetching events';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while fetching events');
  }
};

// Fetches traffic density data for a building within a given date/time range.
// NOTE: The endpoint '/statistics/getTrafficDensity' is NOT defined in the current Postman collection.
// The response type ApiTrafficDensityData is speculative, based on frontend usage.
export const getTrafficDensity = async (
    buildingId: number,
    fromDate: string, // YYYY-MM-DD
    toDate: string,   // YYYY-MM-DD
    // Consider adding time parameters if API supports it: fromTime, toTime
): Promise<ApiTrafficDensityData> => {
    console.warn(
        `getTrafficDensity called with buildingId: ${buildingId}, from: ${fromDate}, to: ${toDate}. ` +
        `This is a STUB. Actual API endpoint and response structure are TBD.`
    );
    // Return an empty array matching the speculative structure to avoid runtime errors in UI
    return Promise.resolve([]);
};

// --- Performance Data Endpoints ---

export async function getSolutrakUpDownTimeDataPoints(
    deviceId: number,
    beginningDate: string, 
    endingDate: string
): Promise<UptimeDataPoint[]> {
    try {
        const response = await API.get<SolutrakResponse<UptimeDataPoint[]>>('/devicePerformances/getUpDownTimeDataPoints', {
            params: { deviceId, beginningDate, endingDate },
        });
        if (!response.data?.IsSuccess) {
            const errorMessage = response.data?.Message ?? 'Failed to fetch uptime data points';
            console.error(errorMessage, response.data);
            throw new Error(errorMessage);
        }
        return response.data.Data ?? [];
    } catch (error) {
        console.error('Error in getSolutrakUpDownTimeDataPoints:', error);
        if (axios.isAxiosError(error)) {
            const errorMessage = error.response?.data?.Message ?? error.message ?? 'Network error fetching uptime data';
            throw new Error(errorMessage);
        } else if (error instanceof Error) {
            throw error;
        }
        throw new Error('An unexpected error occurred while fetching uptime data');
    }
}

export async function getSolutrakUpDownDirectionMinutesDataPoints(
    deviceId: number,
    date: string,
): Promise<UpDownDirectionMinutesDataPoint> {
    try {
        // devicePerformances/getUpDownDirectionMinuteDataPoints?deviceId=340&beginningDate=2025-1-9&endingDate=2025-1-9
        const response = await API.get<SolutrakResponse<UpDownDirectionMinutesDataPoint>>(
            '/devicePerformances/getUpDownDirectionMinuteDataPoints', {
            params: { deviceId, beginningDate: date, endingDate: date },
        });
        if (!response.data?.IsSuccess) {
            const errorMessage = response.data?.Message ?? 'Failed to fetch up/down direction minutes data points';
            console.error(errorMessage, response.data);
            throw new Error(errorMessage);
        }
        return response.data.Data;
    } catch (error) {
        console.error('Error in getSolutrakUpDownDirectionMinutesDataPoints:', error);
        if (axios.isAxiosError(error)) {
            const errorMessage = error.response?.data?.Message ?? error.message ?? 'Network error fetching up/down direction minutes data';
            throw new Error(errorMessage);
        } else if (error instanceof Error) {
            throw error;
        }
        throw new Error('An unexpected error occurred while fetching up/down direction minutes data');
    }
}

export async function getSolutrakDoorCycleCounts(
    deviceId: number,
    beginningDate: string, 
    endingDate: string
): Promise<DoorCycleCountsDataPoint[]> {
    try {
        const response = await API.get<SolutrakResponse<DoorCycleCountsDataPoint[]>>('/devicePerformances/getDoorCycleCounts', {
            params: { deviceId, beginningDate, endingDate },
        });
        if (!response.data?.IsSuccess) {
            const errorMessage = response.data?.Message ?? 'Failed to fetch door cycle counts';
            console.error(errorMessage, response.data);
            throw new Error(errorMessage);
        }
        return response.data.Data ?? [];
    } catch (error) {
        console.error('Error in getSolutrakDoorCycleCounts:', error);
        if (axios.isAxiosError(error)) {
            const errorMessage = error.response?.data?.Message ?? error.message ?? 'Network error fetching door cycle counts';
            throw new Error(errorMessage);
        } else if (error instanceof Error) {
            throw error;
        }
        throw new Error('An unexpected error occurred while fetching door cycle counts');
    }
}

export async function getSolutrakRunCounts(
    deviceId: number,
    beginningDate: string, 
    endingDate: string
): Promise<RunCountsDataPoint[]> {
    try {
        const response = await API.get<SolutrakResponse<RunCountsDataPoint[]>>('/devicePerformances/getRunCounts', {
            params: { deviceId, beginningDate, endingDate },
        });
        if (!response.data?.IsSuccess) {
            const errorMessage = response.data?.Message ?? 'Failed to fetch run counts';
            console.error(errorMessage, response.data);
            throw new Error(errorMessage);
        }
        return response.data.Data ?? [];
    } catch (error) {
        console.error('Error in getSolutrakRunCounts:', error);
        if (axios.isAxiosError(error)) {
            const errorMessage = error.response?.data?.Message ?? error.message ?? 'Network error fetching run counts';
            throw new Error(errorMessage);
        } else if (error instanceof Error) {
            throw error;
        }
        throw new Error('An unexpected error occurred while fetching run counts');
    }
}

export async function getSolutrakLast24HoursCarCallCountDataPoints(
    deviceId: number,
    beginningDate: string, 
    endingDate: string // Note: API docs mention endingDate might be ignored for this endpoint
): Promise<HourlyCarCallDataPoint[]> {
    try {
        const response = await API.get<SolutrakResponse<HourlyCarCallDataPoint[]>>('/devicePerformances/getLast24HoursCarCallCountDataPoints', {
            params: { deviceId, beginningDate, endingDate },
        });
        if (!response.data?.IsSuccess) {
            const errorMessage = response.data?.Message ?? 'Failed to fetch last 24 hours car call counts';
            console.error(errorMessage, response.data);
            throw new Error(errorMessage);
        }
        return response.data.Data ?? [];
    } catch (error) {
        console.error('Error in getSolutrakLast24HoursCarCallCountDataPoints:', error);
        if (axios.isAxiosError(error)) {
            const errorMessage = error.response?.data?.Message ?? error.message ?? 'Network error fetching 24h car call data';
            throw new Error(errorMessage);
        } else if (error instanceof Error) {
            throw error;
        }
        throw new Error('An unexpected error occurred while fetching 24h car call data');
    }
}

export async function getSolutrakDoorCycleDataPoints(
    deviceId: number,
    beginningDate: string, 
    endingDate: string
): Promise<DoorCyclePerFloorDataPoint[]> {
    try {
        const response = await API.get<SolutrakResponse<DoorCyclePerFloorDataPoint[]>>('/devicePerformances/getDoorCycleDataPoints', {
            params: { deviceId, beginningDate, endingDate },
        });
        if (!response.data?.IsSuccess) {
            const errorMessage = response.data?.Message ?? 'Failed to fetch door cycle data points';
            console.error(errorMessage, response.data);
            throw new Error(errorMessage);
        }
        // API returns Count as string, ensure it's handled appropriately if conversion is needed later
        return response.data.Data ?? [];
    } catch (error) {
        console.error('Error in getSolutrakDoorCycleDataPoints:', error);
        if (axios.isAxiosError(error)) {
            const errorMessage = error.response?.data?.Message ?? error.message ?? 'Network error fetching door cycle data points';
            throw new Error(errorMessage);
        } else if (error instanceof Error) {
            throw error;
        }
        throw new Error('An unexpected error occurred while fetching door cycle data points');
    }
}

export async function getSolutrakLoadLevelDataPoints(
    deviceId: number,
    beginningDate: string, 
    endingDate: string
): Promise<LoadLevelDataPoint[]> {
    try {
        const response = await API.get<SolutrakResponse<LoadLevelDataPoint[]>>('/devicePerformances/getLoadLevelDataPoints', {
            params: { deviceId, beginningDate, endingDate },
        });
        if (!response.data?.IsSuccess) {
            const errorMessage = response.data?.Message ?? 'Failed to fetch load level data points';
            console.error(errorMessage, response.data);
            throw new Error(errorMessage);
        }
        return response.data.Data ?? [];
    } catch (error) {
        console.error('Error in getSolutrakLoadLevelDataPoints:', error);
        if (axios.isAxiosError(error)) {
            const errorMessage = error.response?.data?.Message ?? error.message ?? 'Network error fetching load level data';
            throw new Error(errorMessage);
        } else if (error instanceof Error) {
            throw error;
        }
        throw new Error('An unexpected error occurred while fetching load level data');
    }
}

export async function getSolutrakControllerTemperatureDataPoint(
    deviceId: number,
    beginningDate: string, 
    endingDate: string
): Promise<ControllerTemperatureDataPoint | null | undefined> {
    try {
        const response = await API.get<SolutrakResponse<ControllerTemperatureDataPoint>>('/devicePerformances/getControllerTemperature', {
            params: { deviceId, beginningDate, endingDate },
        });
        if (!response.data?.IsSuccess) {
            const errorMessage = response.data?.Message ?? 'Failed to fetch controller temperature data points';
            console.error(errorMessage, response.data);
            throw new Error(errorMessage);
        }
        return response.data.Data;
    } catch (error) {
        console.error('Error in getSolutrakControllerTemperatureDataPoints:', error);
        if (axios.isAxiosError(error)) {
            const errorMessage = error.response?.data?.Message ?? error.message ?? 'Network error fetching controller temperature';
            throw new Error(errorMessage);
        } else if (error instanceof Error) {
            throw error;
        }
        throw new Error('An unexpected error occurred while fetching controller temperature');
    }
}

// --- Interfaces for getFaultLogs ---

export interface ApiFaultLog {
  Code: string;        // Example: "ASM001"
  Description: string; // Example: "Brake Drop Fault"
  Floor: string;       // Example: "3"
  FaultCount: number;  // Example: 3
}

export interface GetFaultLogsResponse {
  IsSuccess: boolean;
  Message: string;
  Data?: ApiFaultLog[];
}

// Parameter interface for the getFaultLogs function
export interface GetFaultLogsParams {
  deviceId: number;         // Or string, confirm from actual usage/docs if not number
  beginningDate: string;  // Expected format YYYY-MM-DD
  endingDate: string;     // Expected format YYYY-MM-DD
}

export const getFaultLogs = async (params: GetFaultLogsParams): Promise<ApiFaultLog[]> => {
  try {
    const response = await API.get<GetFaultLogsResponse>('/devicePerformances/getFaultLogs', {
      params: {
        deviceId: params.deviceId,
        beginningDate: params.beginningDate,
        endingDate: params.endingDate,
      },
    });

    if (response.data?.IsSuccess) {
      return response.data?.Data ?? [];
    } else {
      const errorMessage = response.data?.Message ?? 'Failed to fetch fault logs';
      console.error(errorMessage, response.data);
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('Error in getFaultLogs:', error);
    if (axios.isAxiosError(error)) {
      const errorMessage = error.response?.data?.Message ?? error.message ?? 'Network error fetching fault logs';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while fetching fault logs');
  }
};

// --- Maintenance Logs --- 

// Interface for a single maintenance log item from the API
export interface ApiMaintenanceLogItem {
  Date: string;       // ISO timestamp e.g., "2025-1-9T15:23:14.6624131+00:00"
  Devices: string;    // Comma-separated string of device IDs e.g., "1,2,3,4"
  Code: string;       // Maintenance operation code e.g., "ASM002"
  Description: string; // Summary of the work done e.g., "Monthly Maintenance"
}

// API Response structure for getMaintenanceLogs
export interface GetMaintenanceLogsResponse {
  IsSuccess: boolean;
  Message: string;
  Data?: ApiMaintenanceLogItem[];
}

// Parameter interface for the getMaintenanceLogs function
export interface GetMaintenanceLogsParams {
  deviceId: number; // Based on Postman, deviceId is an Int
  beginningDate: string;  // Expected format YYYY-MM-DD
  endingDate: string;     // Expected format YYYY-MM-DD
}

// Fetches maintenance logs for a specific device and date range.
export const getMaintenanceLogs = async (params: GetMaintenanceLogsParams): Promise<ApiMaintenanceLogItem[]> => {
  try {
    const response = await API.get<GetMaintenanceLogsResponse>('/devicePerformances/getMaintenanceLogs', {
      params: {
        deviceId: params.deviceId,
        beginningDate: params.beginningDate,
        endingDate: params.endingDate,
      },
    });

    if (response.data?.IsSuccess) {
      return response.data?.Data ?? [];
    } else {
      const errorMessage = response.data?.Message ?? 'Failed to fetch maintenance logs';
      console.error(errorMessage, response.data);
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('Error in getMaintenanceLogs:', error);
    if (axios.isAxiosError(error)) {
      const errorMessage = error.response?.data?.Message ?? error.message ?? 'Network error fetching maintenance logs';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while fetching maintenance logs');
  }
};

export interface ApiRunCountsItem {
    Up: number;    // Number of runs going up
    Down: number;  // Number of runs going down
    Total: number; // Total number of runs
}

export interface GetRunCountsResponse {
    IsSuccess: boolean;
    Message: string;
    Data?: ApiRunCountsItem[];
}

export interface ApiDoorCycleCountsItem {
    Open: number;  // Count of door openings
    Close: number;  // Count of door closings
    Nudging: number;  // Count of nudging events
    Reopen: number;  // Count of door re-openings
}

export interface GetDoorCycleCountsResponse {
    IsSuccess: boolean;
    Message: string;
    Data?: ApiDoorCycleCountsItem[];
}

export interface GetGeneralOperatingDataParams {
    deviceId: number; // Device ID to fetch door cycle counts for
    beginningDate: string; // Expected format YYYY-MM-DD
    endingDate: string;    // Expected format YYYY-MM-DD
}

export interface GeneralOperatingData {
    deviceId: number; // Device ID for which the data is fetched
    TotalRunCount: number;  // Total number of runs
    TotalUpCount: number;   // Total number of runs going up
    TotalDownCount: number; // Total number of runs going down
    TotalOpeningCount: number; // Total number of door openings
    TotalClosingCount: number; // Total number of door closings
    TotalNudgingCount: number; // Total number of nudging events
    TotalReopenCount: number; // Total number of door re-openings
}

export interface DeviceStatisticsData {
    device?: ApiDeviceInfo;
    uptime?: UptimeDataPoint[];
    upDownHours?: UpDownDirectionMinutesDataPoint;
    generalOperatingData?: GeneralOperatingData;
}

export async function getDeviceStatistics(
    deviceId: number,
    date: string,
): Promise<DeviceStatisticsData> {
    const beginningDate = date;
    const endingDate = date;

    const [
        buildingInfos,
        uptime,
        upDownHours,
        generalOperatingData
    ] = await Promise.all([
        getBuildingInfos(),
        getSolutrakUpDownTimeDataPoints(deviceId, beginningDate, endingDate),
        getSolutrakUpDownDirectionMinutesDataPoints(deviceId, date),
        getGeneralOperatingDataForDevice({ deviceId, beginningDate, endingDate }),
    ]);

    const device = buildingInfos
        .flatMap(building => building.bankInfos.flatMap(bank => bank.deviceInfos))
        .find(d => d.deviceId === deviceId);

    return {
        device,
        uptime,
        upDownHours,
        generalOperatingData,
    };
}

export async function getGeneralOperatingDataForDevice(params: GetGeneralOperatingDataParams): Promise<GeneralOperatingData> {

    try {
        const runCountsResponse = await API.get<GetRunCountsResponse>('/devicePerformances/getRunCounts', {
            params: {
                deviceId: params.deviceId,
                beginningDate: params.beginningDate,
                endingDate: params.endingDate,
            },
        });

        if (!runCountsResponse.data?.IsSuccess) {
            const errorMessage = runCountsResponse.data?.Message ?? 'Failed to fetch run counts';
            console.error(errorMessage, runCountsResponse.data);
            throw new Error(errorMessage);
        }
        const runCountsData = runCountsResponse.data.Data ?? [];

        const totalRunCount = runCountsData.reduce((sum, item) => sum + (item.Total || 0), 0);
        const totalUpCount = runCountsData.reduce((sum, item) => sum + (item.Up || 0), 0);
        const totalDownCount = runCountsData.reduce((sum, item) => sum + (item.Down || 0), 0);

        const doorCycleCountsResponse = await API.get<GetDoorCycleCountsResponse>('/devicePerformances/getDoorCycleCounts', {
            params: {
                deviceId: params.deviceId,
                beginningDate: params.beginningDate,
                endingDate: params.endingDate,
            },
        });

        if (!doorCycleCountsResponse.data?.IsSuccess) {
            const errorMessage = doorCycleCountsResponse.data?.Message ?? 'Failed to fetch door cycle counts';
            console.error(errorMessage, doorCycleCountsResponse.data);
            throw new Error(errorMessage);
        }

        const doorCycleCountsData = doorCycleCountsResponse.data.Data ?? [];
        const totalOpeningCount = doorCycleCountsData.reduce((sum, item) => sum + (item.Open || 0), 0);
        const totalClosingCount = doorCycleCountsData.reduce((sum, item) => sum + (item.Close || 0), 0);
        const totalNudgingCount = doorCycleCountsData.reduce((sum, item) => sum + (item.Nudging || 0), 0);
        const totalReopenCount = doorCycleCountsData.reduce((sum, item) => sum + (item.Reopen || 0), 0);

        return {
            deviceId: params.deviceId,
            TotalRunCount: totalRunCount,
            TotalUpCount: totalUpCount,
            TotalDownCount: totalDownCount,
            TotalOpeningCount: totalOpeningCount,
            TotalClosingCount: totalClosingCount,
            TotalNudgingCount: totalNudgingCount,
            TotalReopenCount: totalReopenCount,
        };
    } catch (error) {
        console.error('Error in getGeneralOperatingData (Run Counts):', error);
        if (axios.isAxiosError(error)) {
            const errorMessage = error.response?.data?.Message ?? error.message ?? 'Network error fetching run counts';
            throw new Error(errorMessage);
        } else if (error instanceof Error) {
            throw error;
        }
        throw new Error('An unexpected error occurred while fetching run counts');
    }
}

export async function getGeneralOperatingDataForDevices(params: {
    deviceIds: number[]; // Array of device IDs to fetch data for
    beginningDate: string; // Expected format YYYY-MM-DD
    endingDate: string;    // Expected format YYYY-MM-DD
}): Promise<GeneralOperatingData[]> {
    const results: GeneralOperatingData[] = [];
    for (const deviceId of params.deviceIds) {
        const data = await getGeneralOperatingDataForDevice({
            deviceId,
            beginningDate: params.beginningDate,
            endingDate: params.endingDate,
        });
        results.push(data);
    }
    return results;
}
